import type { UserRole } from "@employee-management/common";
import { useNavigate } from "@tanstack/react-router";
import { Loader2 } from "lucide-react";
import { useEffect } from "react";
import { useAuth } from "@/lib/auth-context";

interface ProtectedRouteProps {
	children: React.ReactNode;
	requiredRole?: UserRole;
	fallback?: React.ReactNode;
}

export function ProtectedRoute({
	children,
	requiredRole,
	fallback,
}: ProtectedRouteProps) {
	const { isAuthenticated, isLoading, user, hasRole } = useAuth();
	const navigate = useNavigate();

	useEffect(() => {
		if (!isLoading && !isAuthenticated) {
			navigate({ to: "/login" });
		}
	}, [isLoading, isAuthenticated, navigate]);

	// Show loading spinner while checking authentication
	if (isLoading) {
		return (
			<div className="min-h-screen flex items-center justify-center">
				<div className="flex items-center space-x-2">
					<Loader2 className="h-6 w-6 animate-spin" />
					<span>Loading...</span>
				</div>
			</div>
		);
	}

	// Redirect to login if not authenticated
	if (!isAuthenticated) {
		return null; // Will redirect in useEffect
	}

	// Check role requirements
	if (requiredRole && !hasRole(requiredRole)) {
		if (fallback) {
			return <>{fallback}</>;
		}

		return (
			<div className="min-h-screen flex items-center justify-center">
				<div className="text-center">
					<h1 className="text-2xl font-bold text-red-600 mb-2">
						Access Denied
					</h1>
					<p className="text-muted-foreground">
						You don't have permission to access this page.
					</p>
					<p className="text-sm text-muted-foreground mt-2">
						Required role: {requiredRole}
						{user && ` | Your role: ${user.role}`}
					</p>
				</div>
			</div>
		);
	}

	return <>{children}</>;
}
